import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import "./SalaryRegister.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import IconButton from "../../components/IconButton/IconButton";
import { Icon } from "@iconify/react";
import ColoredCheckbox from "../../components/ColoredCheckbox/ColoredCheckbox";
import { toast } from "react-toastify";

const SalaryRegister = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Dynamic columns state - updated to include otherDeductions
  const [dynamicColumns, setDynamicColumns] = useState({
    earningsColumns: [],
    employeeDeductionColumns: [],
    otherDeductionColumns: [], // New column for other deductions
    employerContributionColumns: [],
    allowancesColumns: [], // New column for allowances
  });

  // Months array for selection
  const months = [
    { number: 1, name: "January", emoji: "❄️" },
    { number: 2, name: "February", emoji: "💝" },
    { number: 3, name: "March", emoji: "🌸" },
    { number: 4, name: "April", emoji: "🌷" },
    { number: 5, name: "May", emoji: "🌺" },
    { number: 6, name: "June", emoji: "☀️" },
    { number: 7, name: "July", emoji: "🌞" },
    { number: 8, name: "August", emoji: "🌻" },
    { number: 9, name: "September", emoji: "🍂" },
    { number: 10, name: "October", emoji: "🎃" },
    { number: 11, name: "November", emoji: "🦃" },
    { number: 12, name: "December", emoji: "🎄" },
  ];

  // Updated function to analyze data and extract unique column names for each section
  const analyzeDynamicColumns = (data) => {
    if (!data || data.length === 0) return;

    const fixedEarningsSet = new Set();
    const earnedEarningsSet = new Set();
    const employeeDeductionSet = new Set();
    const otherDeductionSet = new Set(); // New set for other deductions
    const employerContributionSet = new Set();
    const allowancesSet = new Set(); // New set for allowances

    data.forEach((record) => {
      (record.fixedEarnings || []).forEach(
        (c) => c.salaryName && fixedEarningsSet.add(c.salaryName)
      );
      (record.earnedEarnings || []).forEach(
        (c) => c.salaryName && earnedEarningsSet.add(c.salaryName)
      );
      (record.employeeContribution || []).forEach(
        (c) => c.salaryName && employeeDeductionSet.add(c.salaryName)
      );
      (record.otherDeductions || []).forEach(
        (c) => c.salaryName && otherDeductionSet.add(c.salaryName)
      );
      (record.employerContribution || []).forEach(
        (c) => c.salaryName && employerContributionSet.add(c.salaryName)
      );
      (record.allowances || []).forEach(
        (c) => c.salaryName && allowancesSet.add(c.salaryName)
      );
    });

    setDynamicColumns({
      fixedEarningsColumns: Array.from(fixedEarningsSet),
      earnedEarningsColumns: Array.from(earnedEarningsSet),
      employeeDeductionColumns: Array.from(employeeDeductionSet),
      otherDeductionColumns: Array.from(otherDeductionSet),
      employerContributionColumns: Array.from(employerContributionSet),
      allowancesColumns: Array.from(allowancesSet),
    });
  };

  // Updated function to handle the correct backend response structure
  const getComponentValue = (components, componentName) => {
    if (!components || !Array.isArray(components)) return 0;
    const found = components.find((item) => item.salaryName === componentName);
    return found?.value || 0;
  };

  useEffect(() => {
    fetchPayrollSummaries();
  }, []);

  // Add this new useEffect for default selections
  useEffect(() => {
    if (payrolls.length > 0 && !selectedPayroll) {
      // Select first payroll by default
      const firstPayroll = payrolls[0];
      setSelectedPayroll(firstPayroll);

      // Select current month by default
      const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11
      const currentMonthObj = months.find(
        (month) => month.number === currentMonth
      );
      if (currentMonthObj) {
        setSelectedMonth(currentMonthObj);
        // Automatically fetch data for default selections
        fetchSalaryReport(
          firstPayroll.payrollId,
          currentMonthObj.number,
          selectedYear
        );
      }
    }
  }, [payrolls, selectedYear]); // Run when payrolls data is loaded

  useEffect(() => {
    if (selectedPayroll && selectedMonth) {
      fetchSalaryReport(
        selectedPayroll.payrollId,
        selectedMonth.number,
        selectedYear
      );
    }
  }, [selectedYear]);

  const fetchPayrollSummaries = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/summaries`
      );
      const result = response.data;

      if (result.status === 200) {
        setPayrolls(result.data);
      } else {
        setError(result.message || "Failed to fetch payroll summaries");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching payroll summaries");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalaryReport = async (payrollId, month, yearParam) => {
    setLoading(true);
    setSalaryData([]);

    const year = yearParam || selectedYear;
    const payrollDate = `${year}-${month.toString().padStart(2, "0")}-01`;

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/comp-And-Ben/report/salary-register`,
        {
          payrollId: payrollId,
          payrollDate: payrollDate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = response.data;

      if (result.status === 200) {
        setSalaryData(result.data);
        initializeEditableData(result.data);
        // Analyze the data to extract dynamic columns
        analyzeDynamicColumns(result.data);
      } else {
        toast.error(result.message || "Failed to fetch salary report");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching salary report");
    } finally {
      setLoading(false);
    }
  };

  const initializeEditableData = (data) => {
    const editable = {};
    data.forEach((record, index) => {
      editable[index] = {
        totalReimbursements: record.totalReimbursements || 0,
        // Add other editable fields as needed
      };
    });
    setEditableData(editable);
  };

  const handlePayrollSelection = (payroll) => {
    setSelectedPayroll(payroll);
    // Don't reset month if we're just changing payroll
    if (selectedMonth) {
      fetchSalaryReport(payroll.payrollId, selectedMonth.number, selectedYear);
    } else {
      setSalaryData([]); // Clear previous data only if no month selected
    }
    setError(null);
  };

  const handleMonthSelection = (month) => {
    setSelectedMonth(month);
    if (selectedPayroll) {
      fetchSalaryReport(selectedPayroll.payrollId, month.number, selectedYear);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIndices = salaryData.map((_, index) => index);
      setSelectedRows(allIndices);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (index) => {
    setSelectedRows((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleBulkDownloadExcel = async () => {
    if (selectedRows.length === 0) {
      toast.info("Please select at least one employee");
      return;
    }

    // Get the actual payroll history IDs from selected rows
    const selectedPayrollHistoryIds = selectedRows
      .map((index) => salaryData[index]?.payrollHistoryId)
      .filter((id) => id);

    if (selectedPayrollHistoryIds.length === 0) {
      toast.error("Invalid selection. Please try again.");
      return;
    }

    setIsDownloading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/payroll-history/download`,
        selectedPayrollHistoryIds, // Send IDs instead of indices
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `Payslips_Bulk_${new Date().toISOString()}.zip`; // Default filename

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); // Use the dynamic filename for the ZIP file
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBulkDownloadPayslip = async () => {
    if (selectedRows.length === 0) {
      toast.info("Please select at least one employee");
      return;
    }

    // Get the actual payroll history IDs from selected rows
    const selectedPayrollHistoryIds = selectedRows
      .map((index) => salaryData[index]?.payrollHistoryId)
      .filter((id) => id);

    if (selectedPayrollHistoryIds.length === 0) {
      toast.error("Invalid selection. Please try again.");
      return;
    }

    setIsDownloading(true);
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/payslip/download/bulk`,
        selectedPayrollHistoryIds, // Send IDs instead of indices
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `payslips-${selectedMonth?.name}-${selectedYear}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Payslips downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSinglePayslipDownload = async (payrollHistoryId) => {
    setIsDownloading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_ACCOUNTANT_URL}/payslip/download/${payrollHistoryId}`,
        {
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `payslip-${payrollHistoryId}.pdf`; // Default filename

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Updated calculateTotals function to include totalDeductions
  const calculateTotals = () => {
    return salaryData.reduce(
      (acc, employee) => {
        acc.totalGross += employee.earnedGrossSalary || 0;
        acc.totalEmployeeContribution +=
          employee.totalEmployeeContribution || 0;
        acc.totalNetPay += employee.earnedNetPay || 0;
        acc.totalEmployerContribution +=
          employee.totalEmployerContribution || 0;
        acc.totalCTC += employee.totalctc || 0;
        return acc;
      },
      {
        totalGross: 0,
        totalEmployeeContribution: 0,
        totalNetPay: 0,
        totalEmployerContribution: 0,
        totalCTC: 0,
      }
    );
  };
  return (
    <div className="salary-registry">
      {/* New Payroll Selection Component */}
      <div className="payroll-selection">
        <div className="payroll-selection-header">
          <h2 className="heading6">Salary Register Report</h2>

          <div className="year-selector">
            <div className="year-arrow-controls">
              <button
                className="year-arrow left"
                aria-label="Previous year"
                type="button"
                disabled={selectedYear <= 1900}
                onClick={() => setSelectedYear((y) => Math.max(1900, y - 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M15.7 5.7a1 1 0 0 1 0 1.4L10.42 12l5.3 4.9a1 1 0 0 1-1.42 1.4l-6-5.5a1 1 0 0 1 0-1.4l6-5.5a1 1 0 0 1 1.4 0z" />
                </svg>
              </button>
              <input
                id="year-input"
                type="number"
                min={1900}
                max={2100}
                className="year-value"
                value={selectedYear}
                onChange={(e) => {
                  let val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 9999)
                    setSelectedYear(val);
                }}
                placeholder="YYYY"
                style={{ width: 70, textAlign: "center" }}
              />
              <button
                className="year-arrow right"
                aria-label="Next year"
                type="button"
                disabled={selectedYear >= 2100}
                onClick={() => setSelectedYear((y) => Math.min(2100, y + 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M8.3 18.3a1 1 0 0 1 0-1.4L13.58 12 8.3 7.1a1 1 0 0 1 1.42-1.4l6 5.5a1 1 0 0 1 0 1.4l-6 5.5a1 1 0 0 1-1.4 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {isLoading ? (
          <div className="payroll-buttons-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="loading-skeleton"
                style={{ height: "64px" }}
              ></div>
            ))}
          </div>
        ) : (
          <div className="payroll-buttons-grid">
            {payrolls.length > 0 ? (
              payrolls.map((payroll) => (
                <button
                  key={payroll.payrollId}
                  onClick={() => handlePayrollSelection(payroll)}
                  className={`payroll-button ${
                    selectedPayroll?.payrollId === payroll.payrollId
                      ? "active"
                      : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="payroll-button-content">
                    <div className="payroll-text">
                      <div className="payroll-name">
                        {payroll.payrollName || `Payroll ${payroll.payrollId}`}
                      </div>
                      <div
                        className={`payroll-type-badge ${
                          payroll.payrollSetupType?.toLowerCase() === "auto"
                            ? "auto"
                            : "manual"
                        }`}
                      >
                        <span className="badge-icon">⚙️</span>
                        {payroll.payrollSetupType}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="error">
                No payroll configurations found. Please create a payroll
                configuration first.
              </div>
            )}
          </div>
        )}

        {/* Month Selection Section */}
        {selectedPayroll && (
          <div className="month-selection">
            <div className="month-cards-grid">
              {months.map((month) => (
                <button
                  key={month.number}
                  onClick={() => handleMonthSelection(month)}
                  className={`month-card ${
                    selectedMonth?.number === month.number ? "active" : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="month-emoji">{month.emoji}</div>
                  <div className="month-name">{month.name}</div>
                  <div className="month-year">{selectedYear}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {error && (
        <div className="salary-registry__error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}
      {loading && <LoadingScreen />}

      {salaryData.length > 0 && (
        <>
          {/* Updated Summary Cards Section - Only Essential Metrics */}
          <div className="salary-registry__summary">
            <div className="stats-grid">
              {/* 1. Total Gross Salary - Most important baseline metric */}
              <div className="stat-card total-gross">
                <div className="stats-card__accent" aria-hidden="true" />
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <h3>{formatCurrency(calculateTotals().totalGross)}</h3>
                  <p>Total Gross Salary</p>
                </div>
              </div>

              {/* 2. Total Employee Deductions - Critical for compliance */}
              <div className="stat-card total-deductions">
                <div className="stats-card__accent" aria-hidden="true" />
                <div className="stat-icon">📉</div>
                <div className="stat-content">
                  <h3>
                    {formatCurrency(
                      calculateTotals().totalEmployeeContribution
                    )}
                  </h3>
                  <p>Total Employee Deductions</p>
                </div>
              </div>

              {/* 3. Total Net Pay - Most important for employees */}
              <div className="stat-card total-net">
                <div className="stats-card__accent" aria-hidden="true" />
                <div className="stat-icon">💵</div>
                <div className="stat-content">
                  <h3>{formatCurrency(calculateTotals().totalNetPay)}</h3>
                  <p>Total Net Pay</p>
                </div>
              </div>

              {/* 4. Total Employer Contribution - Important for cost analysis */}
              <div className="stat-card total-employer">
                <div className="stats-card__accent" aria-hidden="true" />
                <div className="stat-icon">🏢</div>
                <div className="stat-content">
                  <h3>
                    {formatCurrency(
                      calculateTotals().totalEmployerContribution
                    )}
                  </h3>
                  <p>Total Employer Contribution</p>
                </div>
              </div>

              {/* 5. Total CTC - Complete cost overview */}
              <div className="stat-card total-ctc">
                <div className="stats-card__accent" aria-hidden="true" />
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <h3>{formatCurrency(calculateTotals().totalCTC)}</h3>
                  <p>Total CTC</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {salaryData.length > 0 && (
        <>
          <div className="salary-registry__table-container">
            <table className="salary-table salary-table--compact">
              <thead>
                {/* Main Header Row */}
                <tr>
                  <th rowSpan="2">
                    <ColoredCheckbox
                      color="green"
                      name="selectAll"
                      label="Employee Name"
                      checked={
                        selectedRows.length === salaryData.length &&
                        salaryData.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th rowSpan="2">Employee ID</th>
                  <th rowSpan="2" className="neutral-header">
                    Base Days
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Loss Of Pay
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Paid Days
                  </th>

                  {/* Fixed Earnings Group */}
                  <th
                    colSpan={dynamicColumns.fixedEarningsColumns.length}
                    className="earnings-header"
                  >
                    Fixed Earnings
                  </th>
                  <th rowSpan="2" className="earnings-header">
                    Fixed Gross
                  </th>

                  {/* Earned Earnings Group */}
                  <th
                    colSpan={dynamicColumns.earnedEarningsColumns.length}
                    className="fixed-earnings-header"
                  >
                    Earned
                  </th>
                  <th rowSpan="2" className="fixed-earnings-header">
                    Earned Gross
                  </th>

                  {/* Allowances Group - New */}
                  {dynamicColumns.allowancesColumns.length > 0 && (
                    <th
                      colSpan={dynamicColumns.allowancesColumns.length}
                      className="allowances-header"
                    >
                      Allowances
                    </th>
                  )}

                  {/* Other Allowances and Reimbursements */}
                  <th rowSpan="2" className="allowances-header">
                    Total Earned Other Allowance
                  </th>
                  <th rowSpan="2" className="reimbursements-header">
                    Total Reimbursements
                  </th>

                  {/* Employee Deductions Group */}
                  <th
                    colSpan={dynamicColumns.employeeDeductionColumns.length}
                    className="deductions-header"
                  >
                    Employee Deductions
                  </th>
                  <th rowSpan="2" className="deductions-header">
                    Total Employee Deduction
                  </th>

                  {/* Other Deductions Group - New */}
                  {dynamicColumns.otherDeductionColumns.length > 0 && (
                    <th
                      colSpan={dynamicColumns.otherDeductionColumns.length}
                      className="other-deductions-header"
                    >
                      Other Deductions
                    </th>
                  )}
                  <th rowSpan="2" className="other-deductions-header">
                    Total Other Deductions
                  </th>

                  {/* Employer Contributions Group */}
                  <th
                    colSpan={dynamicColumns.employerContributionColumns.length}
                    className="contributions-header"
                  >
                    Employer Contributions
                  </th>
                  <th rowSpan="2" className="contributions-header">
                    Total Employer Contribution
                  </th>

                  <th rowSpan="2" className="neutral-header">
                    Fixed Net Pay
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Earned Net Pay
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Total CTC
                  </th>
                  <th rowSpan="2" className="action-column">
                    Payslip
                  </th>
                </tr>

                {/* Sub Header Row */}
                <tr>
                  {dynamicColumns.fixedEarningsColumns.map((name, i) => (
                    <th key={`fe-${i}`} className="earnings-header">
                      {name}
                    </th>
                  ))}
                  {dynamicColumns.earnedEarningsColumns.map((name, i) => (
                    <th key={`ee-${i}`} className="fixed-earnings-header">
                      {name}
                    </th>
                  ))}
                  {dynamicColumns.allowancesColumns.map((name, i) => (
                    <th key={`all-${i}`} className="allowances-header">
                      {name}
                    </th>
                  ))}
                  {dynamicColumns.employeeDeductionColumns.map((name, i) => (
                    <th key={`ed-${i}`} className="deductions-header">
                      {name}
                    </th>
                  ))}
                  {dynamicColumns.otherDeductionColumns.map((name, i) => (
                    <th key={`od-${i}`} className="other-deductions-header">
                      {name}
                    </th>
                  ))}
                  {dynamicColumns.employerContributionColumns.map((name, i) => (
                    <th key={`ec-${i}`} className="contributions-header">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {salaryData.map((record, index) => (
                  <tr key={index}>
                    <td className="employee-name">
                      <ColoredCheckbox
                        label={record.employeeName}
                        color="green"
                        name={`row-${index}`}
                        checked={selectedRows.includes(index)}
                        onChange={() => handleRowSelect(index)}
                      />
                    </td>
                    <td className="employee-id">
                      {record.userEmployeeId || "N/A"}
                    </td>

                    {/* Updated Static Columns */}
                    <td className="neutral-column">{record.baseDays || 0}</td>
                    <td className="neutral-column">{record.lossOfPay || 0}</td>
                    <td className="neutral-column">{record.paidDays || 0}</td>

                    {/* Dynamic Fixed Earnings */}
                    {dynamicColumns.fixedEarningsColumns.map(
                      (name, colIndex) => (
                        <td
                          key={`fe-val-${colIndex}`}
                          className="currency earnings-column"
                        >
                          {formatCurrency(
                            getComponentValue(record.fixedEarnings, name)
                          )}
                        </td>
                      )
                    )}
                    <td className="currency earnings-column">
                      {formatCurrency(record.fixedGrossSalary || 0)}
                    </td>

                    {/* Dynamic Earned Earnings */}
                    {dynamicColumns.earnedEarningsColumns.map(
                      (name, colIndex) => (
                        <td
                          key={`ee-val-${colIndex}`}
                          className="currency fixed-earnings-column"
                        >
                          {formatCurrency(
                            getComponentValue(record.earnedEarnings, name)
                          )}
                        </td>
                      )
                    )}
                    <td className="currency fixed-earnings-column">
                      {formatCurrency(record.earnedGrossSalary || 0)}
                    </td>

                    {/* Dynamic Allowances - New */}
                    {dynamicColumns.allowancesColumns.map((name, colIndex) => (
                      <td
                        key={`all-val-${colIndex}`}
                        className="currency allowances-column"
                      >
                        {formatCurrency(
                          getComponentValue(record.allowances, name)
                        )}
                      </td>
                    ))}

                    {/* Other Allowances and Reimbursements */}
                    <td className="currency allowances-column">
                      {formatCurrency(record.earnedOtherAllowances || 0)}
                    </td>
                    <td className="currency reimbursements-column">
                      {formatCurrency(record.earnedReImbursements || 0)}
                    </td>

                    {/* Dynamic Employee Deductions */}
                    {dynamicColumns.employeeDeductionColumns.map(
                      (name, colIndex) => (
                        <td
                          key={`ed-val-${colIndex}`}
                          className="currency deductions-column"
                        >
                          {formatCurrency(
                            getComponentValue(record.employeeContribution, name)
                          )}
                        </td>
                      )
                    )}
                    <td className="currency deductions-column">
                      {formatCurrency(record.totalEmployeeContribution || 0)}
                    </td>

                    {/* Dynamic Other Deductions - New */}
                    {dynamicColumns.otherDeductionColumns.map(
                      (name, colIndex) => (
                        <td
                          key={`od-val-${colIndex}`}
                          className="currency other-deductions-column"
                        >
                          {formatCurrency(
                            getComponentValue(record.otherDeductions, name)
                          )}
                        </td>
                      )
                    )}
                    <td className="currency other-deductions-column">
                      {formatCurrency(record.totalDeductions || 0)}
                    </td>

                    {/* Dynamic Employer Contributions */}
                    {dynamicColumns.employerContributionColumns.map(
                      (name, colIndex) => (
                        <td
                          key={`ec-val-${colIndex}`}
                          className="currency contributions-column"
                        >
                          {formatCurrency(
                            getComponentValue(record.employerContribution, name)
                          )}
                        </td>
                      )
                    )}
                    <td className="currency contributions-column">
                      {formatCurrency(record.totalEmployerContribution || 0)}
                    </td>

                    {/* Final Columns */}
                    <td className="currency net-pay neutral-column">
                      {formatCurrency(record.fixedNetPay || 0)}
                    </td>
                    <td className="currency net-pay neutral-column">
                      {formatCurrency(record.earnedNetPay || 0)}
                    </td>
                    <td className="currency total-highlight neutral-column">
                      {formatCurrency(record.totalctc || 0)}
                    </td>
                    <td className="action-column">
                      <IconButton
                        variant="add"
                        icon="mdi:download"
                        onClick={() =>
                          handleSinglePayslipDownload(record.payrollHistoryId)
                        }
                        disabled={isDownloading}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {salaryData.length > 0 && (
        <div className="salary-registry__actions">
          <div className="bulk-actions">
            <span className="selection-info">
              {selectedRows.length} of {salaryData.length} selected
            </span>
            {/* <button
              className="action-btn primary"
              onClick={handleBulkDownloadExcel}
              disabled={isDownloading}
            >
              <Icon icon="mdi:microsoft-excel" />
              Download Excel
            </button> */}
            <button
              className="action-btn success"
              onClick={handleBulkDownloadPayslip}
              disabled={isDownloading}
            >
              <Icon icon="mdi:file-pdf-box" />
              Download Payslips
            </button>
          </div>
        </div>
      )}
      {!loading &&
        !error &&
        salaryData.length === 0 &&
        selectedPayroll &&
        selectedMonth && (
          <div className="salary-registry__empty">
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h3>No Data Available</h3>
              <p>No salary data found for the selected payroll and month.</p>
            </div>
          </div>
        )}
    </div>
  );
};

export default SalaryRegister;
